import { baseRestApi } from "api/rest/baseRestApi";
import { ApiResponse, UrlParam } from "api/rest/types/responses";
import { QuoteStatus } from "types/QuoteStatus";

import { AllowedUpdateStatus, ShipperQuoteRequest, ShipperUpdateQuoteRequestBody } from "./types";

type UrlParams = {
  quoteRequestId: UrlParam;
};

type Body = Partial<ShipperQuoteRequest> & {
  bookedQuoteId?: number;
  bookingNotes?: string;
  proNumber?: string;
  bolNumber?: string;
  resendTo?: { contactId: number }[];
  sendNotification?: boolean;
  reason?: string;
};

type QueryArgs = {
  urlParams: UrlParams;
  body: Body;
};

type ResponseType = ApiResponse<{ quoteRequest: ShipperQuoteRequest }>;

const allowedUpdateStatus: Record<AllowedUpdateStatus, AllowedUpdateStatus> = {
  BOOKED: "BOOKED",
  CLOSED: "CLOSED",
  SENT: "SENT",
};

const isAllowedStatus = (status: QuoteStatus | undefined): status is AllowedUpdateStatus =>
  !!status && status in allowedUpdateStatus;

const mapShipperQuoteRequestToUpdateBody = (quoteRequest: Body): ShipperUpdateQuoteRequestBody => ({
  id: quoteRequest.id,
  additionalNotes: quoteRequest.note,
  deadline: quoteRequest.deadline_respond_at,
  hazardousGoodsDetails: quoteRequest.hazardous_goods_details,
  includeCustomsClearance: quoteRequest.include_customs_clearance,
  incoterms: quoteRequest.incoterms,
  insuranceRequired: quoteRequest.insurance_required,
  isHazardous: quoteRequest.is_hazardous,
  isOverweight: quoteRequest.is_overweight,
  partners: quoteRequest.partners?.map((partner) => ({ contactId: partner.contact_id })),
  referenceNumber: quoteRequest.reference_number,
  internalReferenceNumber: quoteRequest.internal_reference_number,
  hideInternalRefTillDispatch: quoteRequest.hide_internal_ref_till_dispatch,
  routingType: quoteRequest.routing_type,
  status: isAllowedStatus(quoteRequest?.status) ? quoteRequest.status : undefined,
  templateDeleted: quoteRequest.template_deleted,
  goodsValue: quoteRequest.goods_value,
  resendTo: quoteRequest.resendTo,
  reason: quoteRequest.reason,
  sendNotification: quoteRequest.sendNotification,
  stops: quoteRequest.stops?.map<Exclude<ShipperUpdateQuoteRequestBody["stops"], undefined>[0]>((stop) => ({
    start: stop.start,
    end: stop.end,
    isTimeTbd: stop.is_time_tbd ?? false,
    note: stop.note ?? undefined,
    referenceNumber: stop.reference_number ?? undefined,
    bookingNotes: stop.booking_notes ?? undefined,
    address:
      !!stop.address && !!Object.values(stop.address).filter(Boolean).length
        ? {
            id: stop.address?.id,
            name: stop.address?.name,
            address1: stop.address?.address_1,
            address2: stop.address?.address_2,
            city: stop.address?.city,
            zip: stop.address?.zip,
            countryCode: stop.address?.country_code,
            countryName: stop.address?.country_name,
            provinceCode: stop.address?.province_code,
            provinceName: stop.address?.province_name,
            googlePlaceId: stop.address?.google_place_id,
            googlePlaceDescription: stop.address?.google_place_description,
            type: stop.address?.type,
          }
        : stop.address === null
        ? null
        : null,
  })),
  teammates: quoteRequest.teammates?.map((teammate) => ({ contactId: teammate.contact_id })),
  unloadingDetails: quoteRequest.unloading_details,
  bookedQuoteId: quoteRequest.bookedQuoteId,
  fileIds: quoteRequest.fileIds,
  bookingNotes: quoteRequest.bookingNotes,
  proNumber: quoteRequest.proNumber,
  bolNumber: quoteRequest.bolNumber,
  ...(quoteRequest.load_spec
    ? {
        loadSpec: {
          ...quoteRequest.load_spec,
          package_groups: quoteRequest.load_spec.package_groups
            ? quoteRequest.load_spec.package_groups.map(({ id, freight_class, ...pg }) => ({
                ...pg,
                id: typeof id === "string" ? undefined : id,
                freight_class: !!freight_class ? freight_class : undefined,
              }))
            : undefined,
        },
      }
    : {}),
});

module.hot?.accept();

export const updateShipperQuoteRequestApi = baseRestApi
  .enhanceEndpoints({ addTagTypes: ["QUOTE_REQUESTS"] })
  .injectEndpoints({
    overrideExisting: module.hot?.status() === "apply",
    endpoints: (builder) => ({
      updateShipperQuoteRequest: builder.mutation<ResponseType, QueryArgs>({
        query: ({ urlParams, body }) => ({
          method: "PUT",
          url: `shipper/quote-requests/${urlParams.quoteRequestId}`,
          body: mapShipperQuoteRequestToUpdateBody(body),
        }),
        invalidatesTags: (_res, _error, args) => [
          "QUOTE_REQUESTS",
          { type: "QUOTE_REQUESTS", id: args.urlParams.quoteRequestId },
        ],
      }),
    }),
  });

export const { useUpdateShipperQuoteRequestMutation } = updateShipperQuoteRequestApi;
